import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Plus, Upload, X } from 'lucide-react';
import { ProjectService } from '@/services/ProjectService';
import { generateThumbnail } from '@/utils/thumbnailUtils';

const CustomLayerDialog = ({
    projectId,
    onLayerCreated,
    canvasWidth,
    canvasHeight,
    thumbnailSize,
    canvasOffset,
    onFileItemsChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [layerName, setLayerName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const handleFileSelect = e => {
        const file = e.target.files[0];
        handleFile(file);
    };

    const handleFile = file => {
        if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = e => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        handleFile(file);
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleCreate = async () => {
        if (!layerName || !selectedFile) return;

        try {
            setIsUploading(true);

            // Upload to S3 first
            const { s3Key, s3Url } = await ProjectService.uploadFileToS3(selectedFile, projectId, progress =>
                setUploadProgress(progress)
            );

            // Generate thumbnail
            const thumbnail = await generateThumbnail(selectedFile);

            // Create file item after successful upload
            const fileId = String(Date.now() + Math.random());
            const fileItem = {
                id: fileId,
                name: selectedFile.name,
                type: selectedFile.type,
                size: String(selectedFile.size),
                isUploading: false,
                thumbnail,
                notes: '',
                section: 'custom_layers',
                createdAt: new Date().toISOString(),
                s3Key,
                s3Url,
            };

            // Create custom item object for the layer
            const customItem = {
                Type: 'Custom',
                Item_name: layerName,
                Categories: ['Custom'],
                imageUrl: s3Url,
                s3Key,
                // fileItem // Store reference to the file item
            };

            // Update both the layer and file items
            if (onLayerCreated) {
                // await onLayerCreated({
                //     ...customItem,
                //     fileItem
                // });
                await onLayerCreated(customItem, fileItem);
            }

            // Reset form and close dialog
            setLayerName('');
            setSelectedFile(null);
            setPreviewUrl('');
            setIsOpen(false);
        } catch (error) {
            console.error('Error creating custom layer:', error);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <button className="flex items-center gap-2 p-2 w-full bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors mb-4">
                    <Plus size={16} />
                    Add Custom Element
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Create Custom Element</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="layerName" className="text-right">
                            Element Name
                        </Label>
                        <Input
                            id="layerName"
                            value={layerName}
                            onChange={e => setLayerName(e.target.value)}
                            className="col-span-3"
                            placeholder="Enter Element name"
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label className="text-right">Image</Label>
                        <div className="col-span-3">
                            <div
                                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 transition-colors"
                                onClick={() => fileInputRef.current?.click()}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                            >
                                {previewUrl ? (
                                    <div className="relative">
                                        <img src={previewUrl} alt="Preview" className="max-h-40 mx-auto" />
                                        <button
                                            onClick={e => {
                                                e.stopPropagation();
                                                setSelectedFile(null);
                                                setPreviewUrl('');
                                            }}
                                            className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-2">
                                        <Upload className="w-8 h-8 text-gray-400" />
                                        <p className="text-sm text-gray-500">Drop an image here or click to upload</p>
                                        <p className="text-xs text-gray-400">Supports PNG, JPG</p>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/png,image/jpeg"
                                    onChange={handleFileSelect}
                                />
                            </div>
                            {isUploading && (
                                <div className="mt-2">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            setIsOpen(false);
                            setLayerName('');
                            setSelectedFile(null);
                            setPreviewUrl('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={!layerName || !selectedFile || isUploading}>
                        {isUploading ? 'Uploading...' : 'Create Element'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CustomLayerDialog;
